import { create } from "zustand";

import { type ProjectThread } from "#app/utils/projects/threads";

/**
 * Project chat threads for the sidebar Recent slot. The sidebar owns the only
 * fetch loop; the chat page calls `requestReload` instead of fetching again.
 */
type ProjectThreadsStore = {
  /** Which project `threads` belongs to. */
  projectId: string | null;
  threads: ProjectThread[];
  /** Server-side total, so a capped fetch can say so instead of hiding rows. */
  total: number;
  loading: boolean;
  listError: string | null;
  reloadToken: number;
  /**
   * First message typed on the landing screen. The thread does not exist yet
   * when it is composed, so it rides here across the create -> navigate hop and
   * the thread view sends it on mount.
   */
  pendingFirstMessage: {
    threadId: string;
    text: string;
    files: File[];
  } | null;
  /** Opens a load. Switching project drops the previous project's rows. */
  beginLoad: (projectId: string) => void;
  setThreads: (threads: ProjectThread[], total: number) => void;
  setLoading: (loading: boolean) => void;
  setListError: (listError: string | null) => void;
  requestReload: () => void;
  setPendingFirstMessage: (
    pending: { threadId: string; text: string; files: File[] } | null,
  ) => void;
};

export const useProjectThreadsStore = create<ProjectThreadsStore>((set) => ({
  projectId: null,
  threads: [],
  total: 0,
  loading: true,
  listError: null,
  reloadToken: 0,
  pendingFirstMessage: null,
  beginLoad: (projectId) =>
    set((state) =>
      state.projectId === projectId
        ? { loading: true, listError: null }
        : { projectId, threads: [], total: 0, loading: true, listError: null },
    ),
  setThreads: (threads, total) => set({ threads, total }),
  setLoading: (loading) => set({ loading }),
  setListError: (listError) => set({ listError }),
  requestReload: () => set((state) => ({ reloadToken: state.reloadToken + 1 })),
  setPendingFirstMessage: (pendingFirstMessage) => set({ pendingFirstMessage }),
}));
